import { useMutation, useQueryClient } from '@tanstack/react-query';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers.ts';
import { useAuth } from '@clerk/clerk-react';
import type { ContributorDto } from '@/types/dtos/contributors/response/contributor.dto.ts';
import {
  GET_USER_CONTRIBUTORS_QUERY_KEY,
  GET_USER_CONTRIBUTORS_LIST_QUERY_KEY,
  GET_CONTRIBUTOR_SUMMARY_QUERY_KEY,
} from '@/state/queries/contributors/user-contributors.ts';
import type { PatchContributorRoleDto } from '@/types/dtos/contributors/request/patch-contributor-role.dto.ts';
import { GET_EVENT_FLOWS_QUERY_KEY } from '@/state/queries/flows/use-get-event-flows';
import { GET_EVENT_SESSIONS_QUERY_KEY } from '@/state/queries/sessions/use-event-sessions';
import { GET_EVENT_FILTERED_SESSIONS_QUERY_KEY } from '@/state/queries/sessions/use-filtered-event-sessions';
import { GET_EVENT_CONTRIBUTORS_QUERY_KEY } from '@/state/queries/contributors/use-get-event-contributors';
import { GET_SESSION_QUERY_KEY } from '@/state/queries/sessions/use-get-session';
import {
  patchContributorEverywhere,
  resolveContributorEventOriginId,
} from '@/state/mutations/contributors/patch-contributor-cache';

export const usePatchContributorRoleMutation = () => {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (patchContributorRoleData: PatchContributorRoleDto) => {
      const token = await getToken();
      return patchContributorRole(patchContributorRoleData, token ?? '');
    },
    onSuccess: async (data, variables) => {
      patchContributorEverywhere(queryClient, data, resolveContributorEventOriginId(data));
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [GET_USER_CONTRIBUTORS_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [GET_USER_CONTRIBUTORS_LIST_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [GET_EVENT_FLOWS_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [GET_EVENT_SESSIONS_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [GET_EVENT_FILTERED_SESSIONS_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [GET_EVENT_CONTRIBUTORS_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [GET_SESSION_QUERY_KEY] }),
        // Invalidate specific contributor summary query
        queryClient.invalidateQueries({ queryKey: [GET_CONTRIBUTOR_SUMMARY_QUERY_KEY, variables.contributorId] }),
      ]);
    },
  });
};

const patchContributorRole = async (
  patchContributorRoleData: PatchContributorRoleDto,
  token: string,
): Promise<ContributorDto> => {
  const { contributorId, roleId, ...data } = patchContributorRoleData;
  const url = `${import.meta.env.VITE_API_URL}/contributors/${contributorId}/roles/${roleId}`;
  return sendJSONRequest({ url, method: METHOD.PATCH, body: data, token });
};

export default usePatchContributorRoleMutation;
